import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { NavMain } from './NavMain';
import { NavUser } from './NavUser';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSystemSettings } from '@/api/hooks/useEntityConfig';

export function AppSidebar() {
  const location = useLocation();
  const { setOpenMobile, isMobile } = useSidebar();

  // Close mobile sidebar on navigation
  useEffect(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [location.pathname, isMobile, setOpenMobile]);

  // Per-tenant branding from entity_configuration (System Configuration
  // page). The deployment env value is only the fallback for tenants that
  // haven't set a display name — one build can serve many entities, so the
  // brand must follow the login, not the bundle.
  const { data: systemSettings } = useSystemSettings();
  const appTitle =
    systemSettings?.tenantDisplayName
    || import.meta.env.VITE_APP_TITLE
    || 'ActiveID';
  const appSubtitle = systemSettings?.tenantDescription || 'Asset Management';

  // Keep the browser tab title in sync with the tenant brand.
  useEffect(() => {
    if (systemSettings?.tenantDisplayName) {
      document.title = systemSettings.tenantDisplayName;
    }
  }, [systemSettings?.tenantDisplayName]);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="hover:bg-transparent hover:text-inherit"
              asChild
            >
              <Link to="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Package className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{appTitle}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {appSubtitle}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <ScrollArea className="h-full">
          <NavMain />
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}
